import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Button, TreeSelect, Typography, ConfigProvider } from '@app/ui';
import { CloseOutlined } from '@ant-design/icons';
import memoize from 'memoize-one';
import sportsApi from '@app/utils/sportsApi';
import './CollectionsModal.scss';

const { Text } = Typography;

const toTreeOptions = memoize(
    (
        collections: Record<number, { id: number; name: string }>,
        selectAllLabel: string,
    ) => {
        const children = Object.values(collections).map(({ id, name }) => ({
            title: name,
            value: id,
        }));
        return [
            {
                title: selectAllLabel,
                value: 'all',
                selectable: false,
                children,
            },
        ];
    },
);

const assignToCollection = (
    clientId: number,
    collectionId: number,
    postIds: number[],
) =>
    sportsApi
        .url(`/clients/${clientId}/collections/${collectionId}/posts`)
        .json({
            action: 'add',
            domainObject: {
                postIds,
            },
        })
        .post();

type CollectionsModalProps = {
    visible: boolean;
    onClose: () => void;
    onSubmit: (
        clientId: number,
        collectionIds: number[],
        postIds: number[],
    ) => void;
    onError?: (
        clientId: number,
        collectionIds: number[],
        postIds: number[],
    ) => void;
    collections: Record<number, { id: number; name: string }>;
    clientId: number;
    postIds: number[];
    title: string;
    isLoadingCollections: boolean;
};

const CollectionsModal: React.FC<CollectionsModalProps> = ({
    visible,
    onClose,
    onSubmit,
    onError,
    collections,
    clientId,
    postIds,
    title,
    isLoadingCollections,
}) => {
    const { t } = useTranslation('measurement');
    const [selected, setSelected] = useState<Array<string | number>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<string | null>(null);

    const selectAllLabel = t('socialMediaExposure.actions.selectAll');
    const options = useMemo(
        () => toTreeOptions(collections, selectAllLabel),
        [collections, selectAllLabel],
    );

    const modalRoot = useMemo(() => {
        const existing = document.getElementById('assign-collections-modal');
        if (existing) return existing;
        const div = document.createElement('div');
        div.id = 'assign-collections-modal';
        document.body.appendChild(div);
        return div;
    }, []);

    const handleAssign = async () => {
        if (isLoading || selected.length === 0) return;
        const selectedIds = selected.map(Number);
        setIsLoading(true);
        setErrors(null);
        try {
            const promises = selectedIds.map(collectionId =>
                assignToCollection(clientId, collectionId, postIds),
            );
            await Promise.all(promises);
            await onSubmit(clientId, selectedIds, postIds);
        } catch (e: any) {
            setErrors(e.message || t('socialMediaExposure.modals.assignError'));
            if (onError) {
                onError(clientId, selectedIds, postIds);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (values: Array<string | number>) => {
        if (values.includes('all')) {
            const allValues = Object.values(collections).map(({ id }) => id);
            setSelected(allValues);
        } else {
            setSelected(values);
        }
    };

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div
            className="collections-modal-overlay-v2"
            data-test="collections-modal"
        >
            <div className="collections-modal-box-v2">
                <Button
                    className="collections-modal-close-v2"
                    type="text"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={onClose}
                />

                <Text
                    style={{ fontFamily: 'pnbold' }}
                >{`${title} - ${postIds.length}`}</Text>
                <Text style={{ fontFamily: 'pnregular' }}>
                    {t('socialMediaExposure.modals.selectCollection')}
                </Text>
                <ConfigProvider
                    theme={{
                        token: {
                            fontFamily: 'pnregular',
                        },
                    }}
                >
                    {' '}
                    <TreeSelect
                        treeData={options}
                        value={selected}
                        onChange={handleChange}
                        multiple
                        treeCheckable
                        placeholder={t(
                            'socialMediaExposure.modals.selectCollection',
                        )}
                        style={{ width: '100%' }}
                        disabled={isLoading || isLoadingCollections}
                        getPopupContainer={triggerNode =>
                            triggerNode.parentElement
                        }
                        autoClearSearchValue={false}
                        showSearch
                        treeNodeFilterProp="title"
                        maxTagCount={3}
                        treeDefaultExpandAll
                    />
                </ConfigProvider>

                {errors && (
                    <Text type="danger" style={{ fontFamily: 'pnregular' }}>
                        {errors}
                    </Text>
                )}

                <div className="collections-modal-actions-v2">
                    <Button
                        color="default"
                        variant="solid"
                        onClick={onClose}
                        icon={<CloseOutlined />}
                        style={{ fontFamily: 'pnregular' }}
                    >
                        {t('socialMediaExposure.actions.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        disabled={selected.length === 0}
                        onClick={handleAssign}
                        loading={isLoading}
                        style={{ fontFamily: 'pnregular' }}
                        data-test="collections-modal-submit"
                    >
                        {t('socialMediaExposure.modals.assign')}
                    </Button>
                </div>
            </div>
        </div>,
        modalRoot,
    );
};

export default CollectionsModal;
